import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import styles from "./PlansList.module.scss";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { IPlanModel } from "./models/planModel";
import {
  constructPlanHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useNavigate } from "react-router-dom";
import { classNames } from "primereact/utils";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";

export const PlansAdministration = () => {
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    href: routePaths.planAdd,
  });

  const { data: plansResult, isLoading } = useApiQuery<IPlanModel[]>({
    queryKey: [endpointsMetadata.plan.getAll],
    endpoint: endpointsMetadata.plan.getAll,
    globalIsLoading: false,
  });

  return (
    <ItemsList
      isLoading={isLoading}
      className={styles.root}
      title="Налични планове"
      noItemsText="Няма намерени планове."
      itemTemplate={(item, index, actionButtons, className) => {
        return (
          <PlanItem
            className={className}
            plan={item}
            key={item?.id ?? index}
            isLoading={isLoading}
            actionButtons={actionButtons}
          />
        );
      }}
      swipeLeftActions={[
        {
          method: "delete",
          type: "other",
          endpoint: endpointsMetadata.plan.deletePlan,
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да премахнете този план? От тук нататък няма да можете да зареждате карти с него, а потребителите, които го използват в момента, ще продължат да го използват докато не изтече.",
          iconType: IconType.DeleteIcon,
          validator: (item: IPlanModel) => !item.isDeleted,
          invalidateKeys: [[endpointsMetadata.plan.getAll]],
        },
        {
          method: "put",
          type: "other",
          endpoint: endpointsMetadata.plan.putBackPlan,
          enablePrompt: true,
          promptMessage: "Сигурни ли сте, че искате да възстановите този план?",
          iconType: IconType.PutBackIcon,
          validator: (item: IPlanModel) => item.isDeleted,
          invalidateKeys: [[endpointsMetadata.plan.getAll]],
        },
      ]}
      swipeRightActions={[
        {
          type: "edit",
          endpoint: routePaths.planEdit,
          iconType: IconType.EditIcon,
          validator: (item: IPlanModel) => !item.isDeleted,
        },
      ]}
      items={plansResult ?? []}
      deletedFilter={{
        storageKey: "plans",
        isDeleted: (item: IPlanModel) => !!item.isDeleted,
        sectionTitle: "Изтрити планове",
        toggleLabel: "Покажи изтритите планове",
      }}
      showItemsCount
      enableSearch
      fullHeight
    />
  );
};

interface IPlanItemProps {
  className?: string;
  plan?: IPlanModel;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}
// Updated PlanItem component - clean like your service types
const PlanItem = ({
  className,
  plan,
  isLoading,
  actionButtons,
}: IPlanItemProps) => {
  const navigate = useNavigate();

  const onClick = () => {
    if (plan) {
      const href = constructPlanHref(plan.id);
      navigate(href);
    }
  };

  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_deleted]: plan?.isDeleted,
      })}
      onClick={onClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          width={"22%"}
          value={
            <div className={styles.item_left_name}>
              {plan?.name}
              <span className={styles.item_left_name_id}>(ID: {plan?.id})</span>
            </div>
          }
        />

        <div className={styles.item_left_info}>
          <SkeletonItem
            isLoading={isLoading}
            width={"55%"}
            value={
              <span>
                {plan?.type ? `${plan.type} • ` : ""}{plan?.duration} •{" "}
                {plan?.isUnlimitedVisits
                  ? "Неограничени"
                  : `${plan?.numberOfVisits} посещения`}
              </span>
            }
          />
        </div>
      </div>

      <div className={styles.item_right}>
        <SkeletonItem
          isLoading={isLoading}
          width={"30%"}
          value={
            <span className={styles.item_right_price}>
              {plan?.price?.toFixed(2)} €
            </span>
          }
        />
        {actionButtons}
        <Icon type={IconType.RightChevronIcon} />
      </div>
    </div>
  );
};
